import {
  useCreateStoreProcessTask,
  useDeleteStoreProcessTask,
  useStoreProcessTasks,
  useUpdateStoreProcessTask,
} from '@gozoki/api'
import {
  type StoreProcessTaskResponse,
  type StoreProcessTaskType,
  storeProcessTaskTypeLabel,
  storeProcessTaskTypes,
  storeTaskCompletionStatusLabel,
} from '@gozoki/api-types'
import { FLAGS } from '@gozoki/feature-flags'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm, type Control, type UseFormTrigger } from 'react-hook-form'
import z from 'zod'
import { ConfirmModal } from '~/components/communication/ConfirmModal'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { useToast, type ToastProps } from '~/components/communication/Toast'
import DatePickerRhf from '~/components/inputs/DatePickerRhf'
import SelectRhf from '~/components/inputs/SelectRhf'
import { TimePickerRhf } from '~/components/inputs/TimePickerRhf'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import Page from '~/components/Page'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import {
  useMigratedRight,
  useMissingPermissions,
  withRoutePermissions,
  type PermissionRequirement,
} from '~/utils/rbac'
import { fonts } from '~/utils/theme/fonts.theme'

type TodoTab = 'create' | 'manage'

type ShowToast = (props: ToastProps) => void

/**
 * `stores.store-tasks.task` est une ressource de liaison `record` : le serveur borne chaque tâche à
 * sa boutique (`store_process_tasks.store_id`). L'écran n'affiche donc pas de sélecteur de
 * périmètre ; il se contente d'exposer les actions accordées.
 */
const TASK_READ: PermissionRequirement = ['read', 'stores.store-tasks.task']
const TASK_ADD: PermissionRequirement = ['add', 'stores.store-tasks.task']
const TASK_EDIT: PermissionRequirement = ['edit', 'stores.store-tasks.task']
const TASK_DELETE: PermissionRequirement = ['delete', 'stores.store-tasks.task']

/** L'écran s'ouvre dès qu'un de ses deux onglets est accessible. */
const TASK_PAGE: PermissionRequirement = { any: [TASK_READ, TASK_ADD] }

export const Route = createFileRoute('/_authenticated/store-app-todo/')({
  staticData: {
    permission: TASK_PAGE,
  },
  component: StoreAppTodoRoute,
  validateSearch: z.object({ tab: z.enum(['create', 'manage']).optional() }),
})
withRoutePermissions(Route)

// Écran gated par le flag `store-app-add-process-bo` : planifier et gérer les tâches « process »
// (store-app) d'une boutique.
function StoreAppTodoRoute() {
  const featureEnabled = useFeatureFlag(FLAGS.storeAppAddProcessBo)
  const flagsReady = useFeatureFlagsReady()

  if (!flagsReady) {
    return <p className="p-4 text-sm text-muted-foreground">Chargement…</p>
  }

  if (!featureEnabled) {
    return <Navigate to="/" replace />
  }

  return <StoreAppTodoPage />
}

const TABS: AppTab<TodoTab>[] = [
  { value: 'create', label: 'Créer' },
  { value: 'manage', label: 'Gérer' },
]

function StoreAppTodoPage() {
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [Toast, showToast] = useToast()
  // Tant que `permission-v2` est OFF, le tuple v2 ne discrimine rien : le bit `ADMIN` de l'entrée
  // de menu reste la garde effective de l'écran.
  const canRead = useMigratedRight(TASK_READ, ['ADMIN'])
  const canAdd = useMigratedRight(TASK_ADD, ['ADMIN'])
  const missingPermissions = useMissingPermissions(TASK_PAGE)

  const tabs = TABS.filter((entry) => (entry.value === 'create' ? canAdd : canRead))

  if (tabs.length === 0) return <RbacAccessDeniedPage missingPermissions={missingPermissions} />

  const activeTab: TodoTab = tabs.some((entry) => entry.value === tab)
    ? (tab as TodoTab)
    : tabs[0].value
  const setTab = (next: TodoTab) => navigate({ search: (prev) => ({ ...prev, tab: next }) })

  return (
    <Page sx={{ maxWidth: '720px', margin: 'auto' }}>
      <Toast />
      <Typography style={fonts.pageSubtitle} mb="24px">
        Store App · tâches
      </Typography>

      <AppTabs tabs={tabs} value={activeTab} onTabChange={setTab}>
        {canAdd ? (
          <TabsContent value="create">
            <CreateTaskForm showToast={showToast} />
          </TabsContent>
        ) : null}
        {/* Sans `read`, la liste n'est pas montée : aucune requête vouée à un 403 n'est lancée. */}
        {canRead ? (
          <TabsContent value="manage">
            <ManageTasksTab showToast={showToast} />
          </TabsContent>
        ) : null}
      </AppTabs>
    </Page>
  )
}

// --- Champs communs (type / jour / heure / durée) partagés par la création et l'édition --------

interface TaskFieldsValues {
  type: StoreProcessTaskType | ''
  date: string
  time: string
  duration: number | null // minutes
}

// Les deux formulaires (création, édition) partagent au moins ces champs : on type le composant
// sur `TaskFieldsValues` et l'appelant caste son `control` (superset) vers ce socle commun.
function TaskFormFields({
  control,
  trigger,
}: {
  control: Control<TaskFieldsValues>
  trigger: UseFormTrigger<TaskFieldsValues>
}) {
  return (
    <>
      <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
        Tâche *
      </Typography>
      <SelectRhf
        control={control}
        name="type"
        options={storeProcessTaskTypes.map((value) => ({
          value,
          label: storeProcessTaskTypeLabel[value],
        }))}
      />

      <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
        Jour *
      </Typography>
      <DatePickerRhf control={control} name="date" trigger={trigger} />

      <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
        Heure *
      </Typography>
      <TimePickerRhf control={control} name="time" trigger={trigger} />

      <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
        Durée (minutes) *
      </Typography>
      <Controller
        control={control}
        name="duration"
        render={({ field, fieldState }) => (
          <TextField
            variant="outlined"
            type="number"
            placeholder="Durée en minutes"
            color="info"
            fullWidth
            value={field.value ?? ''}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') {
                field.onChange(null)
                return
              }
              const parsed = Number(raw)
              field.onChange(Number.isNaN(parsed) ? field.value : parsed)
            }}
            onBlur={() => field.onBlur()}
            error={fieldState.error !== undefined}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </>
  )
}

// Combine jour (ISO date) + heure (HH:mm) en un ISO 8601, ou null si les champs sont invalides.
const buildScheduledAt = (values: TaskFieldsValues): string | null => {
  if (!values.type || !values.date || !values.time || !values.duration || values.duration <= 0) {
    return null
  }

  const [hour, minute] = values.time.split(':').map(Number)
  return DateTime.fromISO(values.date).set({ hour, minute, second: 0, millisecond: 0 }).toISO()
}

// --- Onglet Créer ------------------------------------------------------------------------------

interface CreateTaskValues extends TaskFieldsValues {
  storeId: number | null
}

function CreateTaskForm({ showToast }: { showToast: ShowToast }) {
  const { control, trigger, getValues, reset } = useForm<CreateTaskValues>({
    defaultValues: { storeId: null, type: '', date: '', time: '', duration: null },
  })

  const { mutate: createTask, isPending } = useCreateStoreProcessTask()

  const handleSubmit = async () => {
    const valid = await trigger()
    const values = getValues()
    const scheduledAt = buildScheduledAt(values)

    if (!valid || !values.storeId || !values.type || !scheduledAt || !values.duration) {
      showToast({ message: 'Boutique, tâche, jour, heure et durée sont requis', severity: 'error' })
      return
    }

    createTask(
      { storeId: values.storeId, type: values.type, scheduledAt, duration: values.duration },
      {
        onSuccess: () => {
          showToast({ message: 'Tâche planifiée', severity: 'success' })
          reset()
        },
        onError: () => {
          showToast({ message: 'Échec de la création de la tâche', severity: 'error' })
        },
      },
    )
  }

  return (
    <div className="mt-[16px]">
      <Typography mb="8px" style={fonts.inputHeader}>
        Boutique *
      </Typography>
      <Controller
        control={control}
        name="storeId"
        render={({ field }) => (
          <StoreCombobox
            storeType="boutique"
            value={field.value}
            onChange={(value) => field.onChange(value)}
          />
        )}
      />

      <TaskFormFields
        control={control as unknown as Control<TaskFieldsValues>}
        trigger={trigger as unknown as UseFormTrigger<TaskFieldsValues>}
      />

      <Button
        variant="contained"
        sx={{ marginTop: '32px', alignSelf: 'flex-start' }}
        disabled={isPending}
        onClick={handleSubmit}
      >
        Planifier
      </Button>
    </div>
  )
}

// --- Onglet Gérer ------------------------------------------------------------------------------

const formatWhen = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy · HH:mm')

function ManageTasksTab({ showToast }: { showToast: ShowToast }) {
  const [storeId, setStoreId] = useState<number | null>(null)
  const [editing, setEditing] = useState<StoreProcessTaskResponse | null>(null)
  const [deleting, setDeleting] = useState<StoreProcessTaskResponse | null>(null)

  const canEdit = useMigratedRight(TASK_EDIT, ['ADMIN'])
  const canDelete = useMigratedRight(TASK_DELETE, ['ADMIN'])

  const tasksQuery = useStoreProcessTasks(storeId)
  const tasks = tasksQuery.data ?? []

  const { mutateAsync: deleteTask } = useDeleteStoreProcessTask()

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteTask(deleting.id)
      await tasksQuery.refetch()
      showToast({ message: 'Tâche supprimée', severity: 'success' })
    } catch {
      showToast({ message: 'Échec de la suppression', severity: 'error' })
    } finally {
      setDeleting(null)
    }
  }

  const renderTaskList = () => {
    if (storeId === null) {
      return <Typography color="text.secondary">Sélectionnez une boutique.</Typography>
    }
    if (tasksQuery.isLoading) {
      return <Typography color="text.secondary">Chargement…</Typography>
    }
    if (tasks.length === 0) {
      return (
        <Typography color="text.secondary">Aucune tâche planifiée pour cette boutique.</Typography>
      )
    }
    return tasks.map((task) => (
      <div
        key={task.id}
        className="flex items-center gap-[12px] rounded-[12px] border border-[var(--color-border)] p-[12px]"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <Typography fontWeight={600}>
            {storeProcessTaskTypeLabel[task.type]}
            {task.completed ? ` · ✓ fait${task.doneByName ? ` par ${task.doneByName}` : ''}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatWhen(task.scheduledAt)} · {task.duration} min
          </Typography>
          {task.completionStatus ? (
            <Typography
              variant="body2"
              fontWeight={600}
              color={task.completionStatus === 'WITH_ISSUES' ? '#B85B1C' : '#4F6A47'}
            >
              {task.completionStatus === 'WITH_ISSUES' ? '⚠ ' : '✓ '}
              {storeTaskCompletionStatusLabel[task.completionStatus]}
            </Typography>
          ) : null}
          {task.issueComment ? (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              « {task.issueComment} »
            </Typography>
          ) : null}
        </div>
        <IconButton aria-label="Modifier" disabled={!canEdit} onClick={() => setEditing(task)}>
          <Pencil size={18} />
        </IconButton>
        <IconButton
          aria-label="Supprimer"
          color="error"
          disabled={!canDelete}
          onClick={() => setDeleting(task)}
        >
          <Trash2 size={18} />
        </IconButton>
      </div>
    ))
  }

  return (
    <div className="mt-[16px]">
      <Typography mb="8px" style={fonts.inputHeader}>
        Boutique
      </Typography>
      <StoreCombobox storeType="boutique" value={storeId} onChange={setStoreId} />

      <div className="mt-[24px] flex flex-col gap-[8px]">{renderTaskList()}</div>

      {editing ? (
        <EditTaskDialog task={editing} showToast={showToast} onClose={() => setEditing(null)} />
      ) : null}

      <ConfirmModal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Supprimer la tâche ?"
        description="Cette tâche « process » sera définitivement supprimée pour la boutique."
        confirmButton={{ text: 'Supprimer' }}
      />
    </div>
  )
}

// --- Modale d'édition --------------------------------------------------------------------------

interface EditTaskValues extends TaskFieldsValues {
  completed: boolean
}

function EditTaskDialog({
  task,
  showToast,
  onClose,
}: {
  task: StoreProcessTaskResponse
  showToast: ShowToast
  onClose: () => void
}) {
  const scheduled = DateTime.fromISO(task.scheduledAt)
  const { control, trigger, getValues } = useForm<EditTaskValues>({
    defaultValues: {
      type: task.type,
      date: scheduled.toISODate() ?? '',
      time: scheduled.toFormat('HH:mm'),
      duration: task.duration,
      completed: task.completed,
    },
  })

  const { mutate: updateTask, isPending } = useUpdateStoreProcessTask()

  const handleSave = async () => {
    const valid = await trigger()
    const values = getValues()
    const scheduledAt = buildScheduledAt(values)

    if (!valid || !values.type || !scheduledAt || !values.duration) {
      showToast({ message: 'Tâche, jour, heure et durée sont requis', severity: 'error' })
      return
    }

    updateTask(
      {
        id: task.id,
        data: {
          type: values.type,
          scheduledAt,
          duration: values.duration,
          completed: values.completed,
        },
      },
      {
        onSuccess: () => {
          showToast({ message: 'Tâche modifiée', severity: 'success' })
          onClose()
        },
        onError: () => {
          showToast({ message: 'Échec de la modification', severity: 'error' })
        },
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modifier la tâche</DialogTitle>
      <DialogContent>
        <TaskFormFields
          control={control as unknown as Control<TaskFieldsValues>}
          trigger={trigger as unknown as UseFormTrigger<TaskFieldsValues>}
        />
        <Controller
          control={control}
          name="completed"
          render={({ field }) => (
            <FormControlLabel
              sx={{ marginTop: '16px' }}
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Tâche effectuée"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={isPending} onClick={handleSave}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
